import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotify } from 'react-admin';
import {
    fetchProjectFlow,
    updateFlowStep,
    createFlowStep,
    deleteFlowStep,
    createStepComment,
    deleteStepComment,
    fetchCompletionCheck,
    completeProject,
} from './projectFlowApi';
import {
    ProjectFlow,
    ProjectFlowStep,
    ProjectFlowStepComment,
    CompletionCheckResponse,
} from '@/types';

export const useProjectFlow = (projectId: number, enabled = true) => {
    return useQuery({
        queryKey: ['project-flow', projectId, 'with-comments'],
        queryFn: () => fetchProjectFlow(projectId, { includeComments: true }),
        enabled,
        staleTime: 30 * 1000, // 30 seconds
    });
};

export const useStepComments = (
    projectId: number,
    stepId: number,
    enabled = true
) => {
    return useQuery({
        queryKey: ['project-flow-comments', projectId, stepId],
        queryFn: () => fetchProjectFlow(projectId, { includeComments: true }).then(
            flow => {
                const phase = flow.phases.find(p =>
                    p.steps.some(s => s.id === stepId)
                );
                const step = phase?.steps.find(s => s.id === stepId);
                return step?.comments || [];
            }
        ),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes for presigned URLs
        refetchOnWindowFocus: true,
    });
};

export const useUpdateFlowStep = () => {
    const queryClient = useQueryClient();
    const notify = useNotify();

    return useMutation({
        mutationFn: ({
            projectId,
            stepId,
            data,
        }: {
            projectId: number;
            stepId: number;
            data: { status?: string; name?: string };
        }) => updateFlowStep(projectId, stepId, data),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({
                queryKey: ['project-flow', projectId],
            });
        },
        onError: (error: Error) => {
            notify(error.message, { type: 'error' });
        },
    });
};

export const useCreateFlowStep = () => {
    const queryClient = useQueryClient();
    const notify = useNotify();

    return useMutation({
        mutationFn: ({
            projectId,
            data,
        }: {
            projectId: number;
            data: { phase: string; name: string; after_step_id?: number };
        }) => createFlowStep(projectId, data),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({
                queryKey: ['project-flow', projectId],
            });
        },
        onError: (error: Error) => {
            notify(error.message, { type: 'error' });
        },
    });
};

export const useDeleteFlowStep = () => {
    const queryClient = useQueryClient();
    const notify = useNotify();

    return useMutation({
        mutationFn: ({
            projectId,
            stepId,
        }: {
            projectId: number;
            stepId: number;
        }) => deleteFlowStep(projectId, stepId),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({
                queryKey: ['project-flow', projectId],
            });
            notify('Step deleted', { type: 'success' });
        },
        onError: (error: Error) => {
            notify(error.message, { type: 'error' });
        },
    });
};

export const useCreateStepComment = () => {
    const queryClient = useQueryClient();
    const notify = useNotify();

    return useMutation({
        mutationFn: ({
            projectId,
            stepId,
            content,
            files,
        }: {
            projectId: number;
            stepId: number;
            content: string;
            files: File[];
        }) => createStepComment(projectId, stepId, content, files),
        onSuccess: (_, { projectId, stepId }) => {
            queryClient.invalidateQueries({
                queryKey: ['project-flow', projectId],
            });
            queryClient.invalidateQueries({
                queryKey: ['project-flow-comments', projectId, stepId],
            });
        },
        onError: (error: Error) => {
            notify(error.message, { type: 'error' });
        },
    });
};

export const useCompletionCheck = (projectId: number, enabled = false) =>
    useQuery<CompletionCheckResponse>({
        queryKey: ['project-flow-completion-check', projectId],
        queryFn: () => fetchCompletionCheck(projectId),
        enabled,
        staleTime: 0,
    });

export const useCompleteProject = () => {
    const queryClient = useQueryClient();
    const notify = useNotify();

    return useMutation({
        mutationFn: ({
            projectId,
            force,
        }: {
            projectId: number;
            force: boolean;
        }) => completeProject(projectId, force),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({
                queryKey: ['project-flow', projectId],
            });
            queryClient.invalidateQueries({
                queryKey: ['project-flow-completion-check', projectId],
            });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
        onError: (error: Error) => {
            notify(error.message, { type: 'error' });
        },
    });
};

export const useDeleteStepComment = () => {
    const queryClient = useQueryClient();
    const notify = useNotify();

    return useMutation({
        mutationFn: ({
            projectId,
            commentId,
        }: {
            projectId: number;
            commentId: number;
        }) => deleteStepComment(projectId, commentId),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({
                queryKey: ['project-flow', projectId],
            });
            notify('Comment deleted', { type: 'success' });
        },
        onError: (error: Error) => {
            notify(error.message, { type: 'error' });
        },
    });
};
